import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import { showError } from '../../../utils/sweetAlertConfig';
import { API_CONFIG, LOTTO_TYPE, ICON_ACTION } from '../../../contexts/constants';
import { formatTHB } from '../../../utils/numberFormat';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

const ReportMembersWinning = ({ onLogout, userRole, menuName }) => {
  const [searchParams] = useSearchParams();
  const navPeriodId = searchParams.get('periodId');
  const navLotType  = searchParams.get('lotType');
  const navigate = useNavigate();
  const navigatedWithParams = !!(navPeriodId && navLotType);

  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Current step: 1=lottery types, 2=agents, 3=customers
  const [currentStep, setCurrentStep] = useState(1);

  // Hide step 1 flash when navigating in with params
  const [isInitializing, setIsInitializing] = useState(!!(navPeriodId && navLotType));

  // Fixed lottery types (no API call needed)
  const lotteryTypes = [
    { type: 'powerball', typeName: LOTTO_TYPE.powerball },
    { type: 'megamillions', typeName: LOTTO_TYPE.megamillions }
  ];

  // Selected lottery type
  const [selectedLotteryType, setSelectedLotteryType] = useState(null);

  // Table 2: Agents
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Table 3: Customers
  const [customers, setCustomers] = useState([]);

  // Award results for filtering
  const [awardResultsList, setAwardResultsList] = useState([]);

  // Filters
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [selectedAwardIds, setSelectedAwardIds] = useState([]);
  const [selectedAwardIdsDefault, setSelectedAwardIdsDefault] = useState({
		pb: null,
		mg: null
  });

  // Draw period summary for display
  const [drawSummary, setDrawSummary] = useState({ pb: { id: 0, date: '' }, mg: { id: 0, date: '' } });
  const [awardDataMap, setAwardDataMap] = useState({});

	// All useEffect hooks must be at the top level, before any conditional returns
	useEffect(() => {
		fetchAwardResultsWithDefaults();
	}, []);

	// Load agents when step 2 is reached
	useEffect(() => {
		if (currentStep === 2 && selectedLotteryType) {
			fetchAgentsForPeriod();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, selectedLotteryType]);

	// Load customers when step 3 is reached
	useEffect(() => {
		if (currentStep === 3 && selectedAgent) {
			fetchCustomersForAgent(selectedAgent.agentId);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, selectedAgent]);

	if (!['sysadmin', 'owner'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}


	const fetchAwardResultsWithDefaults = async () => {
		try {
			const token = localStorage.getItem('token');
			const headers = { 'Authorization': `Bearer ${token}` };

			const [listRes, summaryRes] = await Promise.all([
				axios.get(`${API_CONFIG.BASE_URL}/report/award-results`, { headers }),
				axios.get(`${API_CONFIG.BASE_URL}/report/order-summary-prize`, { headers })
			]);

			// Build options list and data map
			if (listRes.data && Array.isArray(listRes.data.data)) {
				const options = [
					{ value: '', label: 'ทั้งหมด' },
					...listRes.data.data.map(item => ({
						value: item.id.toString(),
						label: item.label
					}))
				];
				setAwardResultsList(options);

				const dataMap = {};
				listRes.data.data.forEach(item => {
					dataMap[item.id.toString()] = { date: item.label, lot_type: item.lot_type };
				});
				setAwardDataMap(dataMap);

				// Apply navigation params from SummaryPrizesHistory (if present)
				if (navPeriodId) {
					const matchedType = lotteryTypes.find(t => t.type === navLotType);
					if (!matchedType) {
						setIsInitializing(false);
					} else {
						const periodIds = [navPeriodId];

						// Build summary using local dataMap (state hasn't updated yet)
						const navSummary = { pb: { id: 0, date: '' }, mg: { id: 0, date: '' } };
						periodIds.forEach(id => {
							const item = dataMap[id];
							if (!item) return;
							if (item.lot_type === 1) { navSummary.pb.id = id; navSummary.pb.date = item.date; }
							else { navSummary.mg.id = id; navSummary.mg.date = item.date; }
						});

						// Set all state at once — the step-2 useEffect fires with the correct
						// selectedAwardIds already in the closure, so no direct fetch needed
						setSelectedLotteryType(matchedType);
						setSelectedAwardIds(periodIds);
						setDrawSummary(navSummary);
						setCurrentStep(2);
						setIsInitializing(false);
						return; // skip default summary setup below
					}
				}
			}

			// Set default selected IDs and dates from latest draw
			const defaultIds = {};
			const summary = { pb: { id: 0, date: '' }, mg: { id: 0, date: '' } };
			if (summaryRes.data.pb?.award_results_id) {
				defaultIds.pb = summaryRes.data.pb.award_results_id.toString();
				summary.pb = { id: summaryRes.data.pb.award_results_id, date: summaryRes.data.pb.date };
			}
			if (summaryRes.data.mg?.award_results_id) {
				defaultIds.mg = summaryRes.data.mg.award_results_id.toString();
				summary.mg = { id: summaryRes.data.mg.award_results_id, date: summaryRes.data.mg.date };
			}
			if (Object.keys(defaultIds).length > 0) {
				setSelectedAwardIdsDefault(defaultIds);
			}
			setDrawSummary(summary);
		} catch (error) {
			console.error('Error fetching award results:', error);
		}
	};

	const handleSelectLotteryType = async (lotteryType) => {
		setSelectedLotteryType(lotteryType);
		setSelectedAgent(null);
		setAgents([]);
		setCustomers([]);

		const hasDefault = lotteryType.type === 'powerball' && selectedAwardIdsDefault.pb ? [selectedAwardIdsDefault.pb] :
							lotteryType.type === 'megamillions' && selectedAwardIdsDefault.mg ? [selectedAwardIdsDefault.mg] : [];
		setSelectedAwardIds(hasDefault);
		setDrawSummary(buildDrawSummaryFromIds(hasDefault));

		// Move to step 2 and auto-load agents
		setCurrentStep(2);
	};

	const handleBackToLotteryTypes = () => {
		if (navigatedWithParams) {
			navigate(-1);
			return;
		}
		setCurrentStep(1);
		setSelectedLotteryType(null);
		setSelectedAgent(null);
		setAgents([]);
		setCustomers([]);
		setSelectedAwardIds([]);

		const hasDefaultPB = selectedAwardIdsDefault.pb ? [selectedAwardIdsDefault.pb] : [];
		const hasDefaultMG = selectedAwardIdsDefault.mg ? [selectedAwardIdsDefault.mg] : [];
		setDrawSummary(buildDrawSummaryFromIds([...hasDefaultPB, ...hasDefaultMG]));
	};

	const handleBackToAgents = () => {
		setCurrentStep(2);
		setSelectedAgent(null);
		setCustomers([]);
	};

	const handleSelectAgent = async (agent) => {
		setSelectedAgent(agent);
		setCustomers([]);

		// Move to step 3 and auto-load will happen via useEffect
		setCurrentStep(3);
	};

	const buildDrawSummaryFromIds = (ids) => {
		const summary = { pb: { id: 0, date: '' }, mg: { id: 0, date: '' } };
		const pbDates = [], mgDates = [];
		ids.forEach(id => {
			const item = awardDataMap[id];
			if (!item) return;
			if (item.lot_type === 1) { pbDates.push(item.date); summary.pb.id = id; }
			else { mgDates.push(item.date); summary.mg.id = id; }
		});
		summary.pb.date = pbDates.join(', ');
		summary.mg.date = mgDates.join(', ');
		return summary;
	};

	const handleSearch = async () => {
		setDrawSummary(buildDrawSummaryFromIds(selectedAwardIds));
		if (currentStep === 2) {
			// Reload agents with filter
			await fetchAgentsForPeriod();
		} else if (currentStep === 3 && selectedAgent) {
			// Reload customers with filter
			await fetchCustomersForAgent(selectedAgent.agentId);
		}
	};

	const handleClearSearch = () => {
		setDrawSummary({ pb: { id: 0, date: '' }, mg: { id: 0, date: '' } });
		setSelectedAwardIds([]);
		if (currentStep === 2) {
			fetchAgentsForPeriod();
		} else if (currentStep === 3 && selectedAgent) {
			fetchCustomersForAgent(selectedAgent.agentId);
		}
	};

	const fetchAgentsForPeriod = async () => {
		try {
			setLoadingAgents(true);

			// Build query params
			const params = new URLSearchParams();
			params.append('lotteryType', selectedLotteryType.type);

			// Add award period filters if selected
			if (selectedAwardIds.length > 0) {
				selectedAwardIds.forEach(id => params.append('period', id));
			}

			const response = await axios.get(`${API_CONFIG.BASE_URL}/report/agents-by-lottery-type?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				setAgents(response.data.data.map(agent => ({
					agentId: agent.agent_id,
					agentName: agent.agent_name,
					totalOrders: agent.total_orders,
					totalCustomers: agent.total_customers,
					totalTickets: agent.total_tickets,
					totalPriceBeforeDiscount: agent.total_price_before_discount,
					totalDiscount: agent.total_discount,
					totalPriceAfterDiscount: agent.total_price_after_discount ?? ((agent.total_price_before_discount || 0) - (agent.total_discount || 0)),
					totalWins: agent.total_wins,
					totalPrizeAmount: agent.total_prize_amount || 0
				})));
				return true;
			}
			return false;
		} catch (error) {
			console.error('Error fetching agents:', error);
			showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลตัวแทนได้');
			return false;
		} finally {
			setLoadingAgents(false);
		}
	};

	const fetchCustomersForAgent = async (agentId) => {
		try {
			setLoadingCustomers(true);

			// Build query params
			const params = new URLSearchParams();
			params.append('lotteryType', selectedLotteryType.type);
			params.append('agentId', agentId);

			// Add award period filters if selected
			if (selectedAwardIds.length > 0) {
				selectedAwardIds.forEach(id => params.append('period', id));
			}

			const response = await axios.get(`${API_CONFIG.BASE_URL}/report/customers-by-agent-lottery?${params.toString()}`, {
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				setCustomers(response.data.data.map(customer => ({
					customerId: customer.customer_id,
					customerName: customer.customer_name,
					totalOrders: customer.total_orders,
					totalTickets: customer.total_tickets,
					totalPriceBeforeDiscount: customer.total_price_before_discount,
					totalDiscount: customer.total_discount,
					totalPriceAfterDiscount: customer.total_price_after_discount ?? ((customer.total_price_before_discount || 0) - (customer.total_discount || 0)),
					totalWins: customer.total_wins,
					totalPrizeAmount: customer.total_prize_amount || 0
				})));
				return true;
			}
			return false;
		} catch (error) {
			console.error('Error fetching customers:', error);
			showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลลูกค้าได้');
			return false;
		} finally {
			setLoadingCustomers(false);
		}
	};

	// Render Table 1: Lottery Types (Fixed)
	const renderLotteryTypesTable = () => {
		return (
			<div className="mb-6">
				<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">เลือกประเภท</h3>
				<div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
						<thead className="bg-gray-50 dark:bg-gray-900">
							<tr>
								<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
									ประเภท
								</th>
							</tr>
						</thead>
						<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
							{lotteryTypes.map((lottery, index) => (
								<tr
									key={index}
									onClick={() => handleSelectLotteryType(lottery)}
									className={`cursor-pointer transition-colors ${
										selectedLotteryType?.type === lottery.type
											? 'bg-blue-100 dark:bg-blue-900'
											: 'hover:bg-gray-50 dark:hover:bg-gray-700'
									}`}
								>
									<td className="px-6 py-4 whitespace-nowrap text-center">
										<span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold	 ${
											lottery.type === 'powerball'
												? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
												: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
										}`} style={{ minWidth: '200px' }}>
											{lottery.typeName}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		);
	};

	// Search Form for filtering periods
	const SearchForm = () => {
		const filteredAwardOptions = awardResultsList.filter(opt => {
			if (!opt.value) return false;
			const item = awardDataMap[opt.value];
			if (!item) return false;
			if (selectedLotteryType?.type === 'powerball') return item.lot_type === 1;
			if (selectedLotteryType?.type === 'megamillions') return item.lot_type === 2;
			return true;
		});

		return (
		<div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-4">
			<div className="grid grid-cols-1 gap-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
						งวดวันที่ {selectedLotteryType && `(${selectedLotteryType.typeName})`}
					</label>
					<Select
						isMulti
						value={filteredAwardOptions.filter(award => selectedAwardIds.includes(award.value))}
						onChange={(selectedOptions) => {
							setSelectedAwardIds(selectedOptions ? selectedOptions.map(opt => opt.value).filter(v => v !== '') : []);
						}}
						options={filteredAwardOptions}
						placeholder="เลือกงวด..."
						className="basic-multi-select"
						classNamePrefix="select"
						isClearable
						styles={{
							control: (base) => ({
								...base,
								minHeight: '42px',
								borderColor: '#d1d5db',
								backgroundColor: 'var(--tw-bg-opacity)',
							}),
							menu: (base) => ({
								...base,
								zIndex: 100,
							})
						}}
					/>
				</div>
			</div>
			<div className="flex justify-end gap-2 mt-4">
				<button
					onClick={handleSearch}
					className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
				>
					{ICON_ACTION.SEARCH}
					ค้นหา
				</button>
				<button
					onClick={handleClearSearch}
					className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
				>
					{ICON_ACTION.CLEAR}
					ล้าง
				</button>
			</div>
		</div>
	);
	};

	// Render Table 2: Agents
	const renderAgentsTable = () => {
		if (!selectedLotteryType) {
			return null;
		}

		return (
			<div className="mb-6">
				<div className="mb-3 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<button
							onClick={handleBackToLotteryTypes}
							className="px-3 py-1 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
						>
							← ย้อนกลับ
						</button>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
							เลือกตัวแทน ({selectedLotteryType.typeName})
						</h3>
					</div>
					<button
						onClick={() => setShowSearchForm(!showSearchForm)}
						className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
							showSearchForm
							? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
							: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
						}`}
					>
						{showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
						<span className='hidden md:inline'>ค้นหา</span>
					</button>
				</div>

				{showSearchForm && <SearchForm />}

				{loadingAgents ? (
					<div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
					</div>
				) : agents.length === 0 ? (
					<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
						<p className="text-gray-500 dark:text-gray-400">ไม่มีข้อมูลตัวแทน กรุณาคลิกค้นหาเพื่อโหลดข้อมูล</p>
					</div>
				) : (
					<div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
							<thead className="bg-gray-50 dark:bg-gray-900">
								<tr>
									<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[150px]">
										ชื่อตัวแทน
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px]">
										ลูกค้า
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[90px]">
										คำสั่งซื้อ
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[70px]">
										ใบ
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px]">
										ราคาก่อนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[100px]">
										ส่วนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[130px]">
										หลังหักส่วนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[90px]">
										ถูกรางวัล
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px]">
										ยอดรางวัล
									</th>
								</tr>
							</thead>
							<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
								{agents.map((agent, index) => (
									<tr
										key={index}
										onClick={() => handleSelectAgent(agent)}
										className={`cursor-pointer transition-colors ${
											selectedAgent?.agentId === agent.agentId
												? 'bg-blue-100 dark:bg-blue-900'
												: 'hover:bg-gray-50 dark:hover:bg-gray-700'
										}`}
									>
										<td className="px-4 py-3 whitespace-nowrap">
											<div className="text-sm font-medium text-blue-600 dark:text-blue-400">
												{agent.agentName}
											</div>
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{agent.totalCustomers}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{agent.totalOrders}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{agent.totalTickets}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{formatTHB(agent.totalPriceBeforeDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{formatTHB(agent.totalDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
											{formatTHB(agent.totalPriceAfterDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-green-600 dark:text-green-400">
											{agent.totalWins}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-blue-600 dark:text-blue-400">
											{formatTHB(agent.totalPrizeAmount)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		);
	};

	// Render Table 3: Customers
	const renderCustomersTable = () => {
		if (!selectedAgent) {
			return null;
		}

		return (
			<div className="mb-6">
				<div className="mb-3 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<button
							onClick={handleBackToAgents}
							className="px-3 py-1 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
						>
							← ย้อนกลับ
						</button>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
							รายชื่อลูกค้า (ตัวแทน {selectedAgent.agentName})
						</h3>
					</div>
					<button
						onClick={() => setShowSearchForm(!showSearchForm)}
						className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
							showSearchForm
							? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
							: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
						}`}
					>
						{showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
						<span className='hidden md:inline'>ค้นหา</span>
					</button>
				</div>

				{showSearchForm && <SearchForm />}

				{loadingCustomers ? (
					<div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
					</div>
				) : customers.length === 0 ? (
					<div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
						<p className="text-gray-500 dark:text-gray-400">ไม่มีข้อมูลลูกค้า กรุณาคลิกค้นหาเพื่อโหลดข้อมูล</p>
					</div>
				) : (
					<div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
							<thead className="bg-gray-50 dark:bg-gray-900">
								<tr>
									<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[150px]">
										ชื่อลูกค้า
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[90px]">
										คำสั่งซื้อ
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[70px]">
										ใบ
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px]">
										ราคาก่อนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[100px]">
										ส่วนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[130px]">
										หลังหักส่วนลด
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[90px]">
										ถูกรางวัล
									</th>
									<th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[120px]">
										ยอดรางวัล
									</th>
								</tr>
							</thead>
							<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
								{customers.map((customer, index) => (
									<tr
										key={index}
										className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
									>
										<td className="px-4 py-3 whitespace-nowrap">
											<div className="text-sm text-gray-900 dark:text-white">
												{customer.customerName}
											</div>
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{customer.totalOrders}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{customer.totalTickets}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{formatTHB(customer.totalPriceBeforeDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
											{formatTHB(customer.totalDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
											{formatTHB(customer.totalPriceAfterDiscount)}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-green-600 dark:text-green-400">
											{customer.totalWins}
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-center text-sm font-medium text-blue-600 dark:text-blue-400">
											{formatTHB(customer.totalPrizeAmount)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		);
	};

  return (
    <Layout title={menuName} onLogout={onLogout} userRole={userRole}>
      {/* แสดงข้อมูล filter */}
      <div className="mb-4">
		<p className="text-sm mt-1 text-gray-500 dark:text-gray-400">
			{drawSummary.pb.id && drawSummary.mg.id ? (
				<>
					รายการผู้ซื้อ {LOTTO_TYPE.powerball} ประจำงวดวันที่ <span className="font-semibold text-gray-900 dark:text-white">{drawSummary.pb.date || 'ทั้งหมด'}</span> และ {LOTTO_TYPE.megamillions} ประจำงวดวันที่ <span className="font-semibold text-gray-900 dark:text-white">{drawSummary.mg.date || 'ทั้งหมด'}</span>
				</>
			) : drawSummary.mg.id ? (
				<>
					รายการผู้ซื้อ {LOTTO_TYPE.megamillions} ประจำงวดวันที่ <span className="font-semibold text-gray-900 dark:text-white">{drawSummary.mg.date || 'ทั้งหมด'}</span>
				</>
			) : drawSummary.pb.id ? (
				<>
					รายการผู้ซื้อ {LOTTO_TYPE.powerball} ประจำงวดวันที่ <span className="font-semibold text-gray-900 dark:text-white">{drawSummary.pb.date || 'ทั้งหมด'}</span>
				</>
			) : ""}
			{selectedAgent && ` — ตัวแทน ${selectedAgent.agentName}`}
		</p>
		<p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
			{currentStep === 1 && 'เลือกประเภทเพื่อดูรายชื่อตัวแทน'}
			{currentStep === 2 && 'เลือกตัวแทนเพื่อดูรายชื่อลูกค้า (สามารถกรองตามงวดได้)'}
			{currentStep === 3 && 'รายชื่อลูกค้าที่ซื้อ (สามารถกรองตามงวดได้)'}
		</p>
      </div>

      {/* Display only current step table */}
      {isInitializing ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {currentStep === 1 && renderLotteryTypesTable()}
          {currentStep === 2 && renderAgentsTable()}
          {currentStep === 3 && renderCustomersTable()}
        </>
      )}
    </Layout>
  );

}

export default ReportMembersWinning;
